"use client";

import { Pause, Play, RotateCcw, RotateCw } from "lucide-react";
import { SpecialEvents, trackEvent } from "@/analytics";
import { useCallback, useEffect, useRef, useState } from "react";

import Hls from "hls.js";
import Image from "next/image";
import { Slider } from "@/shadcn/ui/slider";
import { SpecialRow } from "@punchup/data/legacy/specials";
import {
  VideoLogger,
  VideoPlayerSurface,
  usePodcastPlaybackContextSync,
} from "@punchup/video/client";
import { updateUserPlaybackLocation } from "@punchup/data/client/user/specials";
import { useInterval } from "@punchup/utils/hooks";

// Note: the CLOUDFLARE_CUSTOMER_SUBDOMAIN variable is set in nextjs config instead of using NEXT_PUBLIC_
const hlsSourceUrl = (src: string) =>
  `https://${process.env.CLOUDFLARE_CUSTOMER_SUBDOMAIN}/${src}/manifest/video.m3u8`;

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Extends VideoLogger to stamp is_audio: true on every event so audio specials
// are distinguishable from video specials in Mixpanel.
class AudioVideoLogger extends VideoLogger {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override _logEvent(event: string, data?: any, sendImmediately?: boolean) {
    super._logEvent(event, { is_audio: true, ...data }, sendImmediately);
  }
}

interface SpecialAudioPlayerProps {
  src: string;
  special: SpecialRow;
  posterImg: string;
  lastPlaybackLocation: number;
}

export default function SpecialAudioPlayer({
  src,
  special,
  posterImg,
  lastPlaybackLocation,
}: SpecialAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const firstCanPlayRef = useRef(false);
  // Ref (not state) so onTimeUpdate always sees the current value without a
  // stale closure, and without triggering an extra re-render per tick.
  const isScrubbingRef = useRef(false);
  const loggerRef = useRef<AudioVideoLogger | null>(null);
  // Bump after the logger is created so usePodcastPlaybackContextSync re-runs
  // with a non-null logger and attaches podcast context before any user-triggered
  // events (e.g. VIDEO_SESSION_STARTED) can fire.
  const [, setLoggerVersion] = useState(0);
  usePodcastPlaybackContextSync(loggerRef.current);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hlsError, setHlsError] = useState(false);

  // Initialize or reset the logger when the special changes.
  useEffect(() => {
    if (!loggerRef.current) {
      loggerRef.current = new AudioVideoLogger(
        special,
        false,
        VideoPlayerSurface.ComedianSpecial
      );
      // Trigger one re-render so usePodcastPlaybackContextSync observes the
      // newly-created logger and calls setPodcastContext on it before the user
      // can interact with the player.
      setLoggerVersion((v) => v + 1);
    } else {
      loggerRef.current.setSpecial(special, false);
    }
  }, [special.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // End the session when the component unmounts or the page is hidden.
  useEffect(() => {
    const handlePageHide = () => loggerRef.current?.end("pagehide");
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      loggerRef.current?.end("component-unmounted");
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    firstCanPlayRef.current = false;
    setHlsError(false);
    const url = hlsSourceUrl(src);

    let hls: Hls | null = null;
    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(audio);
      hlsRef.current = hls;

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          setHlsError(true);
          loggerRef.current?.error();
        }
      });
    } else if (audio.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      audio.src = url;
    }

    return () => {
      hls?.destroy();
      hlsRef.current = null;
    };
  }, [src]);

  const saveLocation = useCallback(
    (time: number) => {
      if (time > 0) updateUserPlaybackLocation(special.id, time);
    },
    [special.id]
  );

  useInterval(
    () => {
      if (audioRef.current) saveLocation(audioRef.current.currentTime);
    },
    isPlaying ? 5000 : null
  );

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.paused ? audio.play().catch(() => {}) : audio.pause();
  };

  const skip = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (seconds > 0) {
      loggerRef.current?.skipForward();
    } else {
      loggerRef.current?.skipBackward();
    }
    const cap = audio.duration > 0 ? audio.duration : Infinity;
    audio.currentTime = Math.max(0, Math.min(cap, audio.currentTime + seconds));
  };

  const remaining = Math.max(0, duration - currentTime);

  return (
    <div className="relative isolate overflow-hidden md:rounded-[18px] text-white">
      {/* Gaussian-blurred artwork background — crops halo via scale(1.6) */}
      {posterImg && (
        <div
          className="absolute inset-0 -z-20 blur-[80px] md:blur-[60px] saturate-[1.35]"
          style={{
            backgroundImage: `url(${posterImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: "scale(1.6)",
          }}
        />
      )}
      {/* Dark scrim — guarantees white text contrast on any artwork */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.36) 0%, rgba(0,0,0,0.65) 100%)",
        }}
      />

      <div className="flex flex-col md:flex-row items-center gap-4 md:gap-7 px-[22px] py-7 md:px-7 md:py-7">
        {posterImg && (
          <div className="relative w-[78%] max-w-[280px] md:w-[220px] md:h-[220px] aspect-square md:aspect-auto rounded-2xl overflow-hidden flex-shrink-0 shadow-xl">
            <Image
              src={posterImg}
              alt={special.title}
              fill
              className="object-cover"
            />
          </div>
        )}

        <div className="flex flex-col gap-[22px] flex-1 min-w-0 w-full">
          <h2 className="text-white font-bold text-[22px] md:text-[28px] text-center md:text-left leading-snug line-clamp-2 tracking-tight">
            {special.title}
          </h2>

          {hlsError ? (
            <p className="text-sm text-white/60">
              Unable to load audio. Please refresh and try again.
            </p>
          ) : (
            <div className="flex flex-col md:flex-row items-center gap-[22px] md:gap-[14px]">
              {/* Slider + times — first in DOM so it appears above controls on mobile */}
              <div className="w-full md:flex-1 md:order-2 md:relative min-w-0">
                <Slider
                  min={0}
                  max={Math.floor(duration) || 100}
                  step={1}
                  value={[Math.floor(currentTime)]}
                  onValueChange={([v]) => {
                    isScrubbingRef.current = true;
                    setCurrentTime(v);
                  }}
                  onValueCommit={([v]) => {
                    if (audioRef.current) audioRef.current.currentTime = v;
                    isScrubbingRef.current = false;
                    saveLocation(v);
                  }}
                  containerClassName="mb-0"
                  trackClassName="bg-white/[0.28] h-1"
                  thumbClassName="h-3.5 w-3.5 border-0 bg-white shadow-[0_0_0_4px_rgba(255,255,255,0.22)]"
                />
                {/*
                 * Mobile: in-flow with top margin.
                 * Desktop: absolutely positioned so the times don't contribute to
                 * the flex-row height — this keeps items-center aligned on the
                 * track itself rather than the track+times midpoint.
                 */}
                <div className="flex justify-between text-xs text-white/[0.78] tabular-nums mt-[10px] md:mt-0 md:absolute md:inset-x-0 md:top-full md:pt-2">
                  <span>{formatTime(currentTime)}</span>
                  <span>-{formatTime(remaining)}</span>
                </div>
              </div>

              {/* Controls — second in DOM, reordered to first on desktop */}
              <div className="flex items-center gap-8 md:gap-[14px] md:order-1 flex-shrink-0">
                <button
                  onClick={() => skip(-30)}
                  aria-label="Skip back 30 seconds"
                  className="flex flex-col items-center gap-0.5 w-11 h-11 md:w-11 md:h-11 justify-center text-white/80 hover:text-white transition-colors"
                >
                  <RotateCcw className="w-8 h-8 md:w-6 md:h-6" />
                  <span className="text-xs font-medium">30s</span>
                </button>
                <button
                  onClick={togglePlay}
                  aria-label={isPlaying ? "Pause" : "Play"}
                  className="w-16 h-16 md:w-14 md:h-14 rounded-full bg-white flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
                >
                  {isPlaying ? (
                    <Pause className="w-7 h-7 md:w-[22px] md:h-[22px] text-black fill-black" />
                  ) : (
                    <Play className="w-7 h-7 md:w-[22px] md:h-[22px] text-black fill-black ml-0.5" />
                  )}
                </button>
                <button
                  onClick={() => skip(30)}
                  aria-label="Skip forward 30 seconds"
                  className="flex flex-col items-center gap-0.5 w-11 h-11 md:w-11 md:h-11 justify-center text-white/80 hover:text-white transition-colors"
                >
                  <RotateCw className="w-8 h-8 md:w-6 md:h-6" />
                  <span className="text-xs font-medium">30s</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        onPlay={() => {
          setIsPlaying(true);
          // logger.start() fires SPECIAL_CLICKED_PLAY + VIDEO_SESSION_STARTED
          loggerRef.current?.start(audioRef.current?.currentTime ?? 0);
        }}
        onPause={() => {
          setIsPlaying(false);
          // logger.pause() fires SPECIAL_CLICKED_PAUSE
          loggerRef.current?.pause();
          saveLocation(audioRef.current?.currentTime ?? 0);
        }}
        onTimeUpdate={() => {
          if (!isScrubbingRef.current && audioRef.current) {
            const t = audioRef.current.currentTime;
            setCurrentTime(t);
            // fires VIDEO_VIEWED_3S / 10S / 30S at milestones; accumulates watchtime
            loggerRef.current?.timeUpdated(t);
          }
        }}
        onDurationChange={() => {
          if (audioRef.current) {
            const d = audioRef.current.duration;
            setDuration(d);
            loggerRef.current?.setDuration(d);
          }
        }}
        onSeeking={() => {
          // pause watchtime accumulation during seeks
          loggerRef.current?.startSeek();
        }}
        onSeeked={() => {
          // fires SPECIAL_SEEKING (unless preceded by a skip button, which
          // sets _skipNextSeekEvent so the event is suppressed)
          loggerRef.current?.endSeeking(audioRef.current?.currentTime ?? 0);
        }}
        onCanPlayThrough={() => {
          if (firstCanPlayRef.current) return;
          firstCanPlayRef.current = true;

          const audio = audioRef.current;
          if (!audio) return;

          if (lastPlaybackLocation) {
            const dur = audio.duration;
            if (isFinite(dur) && lastPlaybackLocation / dur < 0.95) {
              audio.currentTime = lastPlaybackLocation;
            }
          }

          trackEvent(SpecialEvents.SPECIAL_INITIALIZED, {
            is_audio: true,
            special_id: special.id,
            isPreview: false,
            location: lastPlaybackLocation || 0,
          });
        }}
        onEnded={() => {
          setIsPlaying(false);
          // fires VIDEO_SESSION_ENDED with watchtime + watched_pct
          loggerRef.current?.end("playback-ended");
          saveLocation(duration);
        }}
      />
    </div>
  );
}
